import type { TaskStatus } from './enterpriseApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
const STORAGE_KEY = 'tax-insight-auth';

export interface AuthQuota {
  preliminaryLimit: number;
  preliminaryUsed: number;
  preliminaryRemaining: number;
}

export interface AuthUser {
  id: string;
  phone: string;
  phoneDigits: string;
  isNewAccount?: boolean;
  quota?: AuthQuota;
}

export interface StoredAuth {
  token: string;
  user: AuthUser;
}

export interface SmsSendResult {
  ok: boolean;
  expiresIn: number;
  resendAfter: number;
}

export interface AnalysisRecordInput {
  clientId: string;
  companyName: string;
  industry?: string | null;
  region?: string | null;
  opportunityScore?: number | null;
  taskId?: string | null;
  companyId?: string | null;
  creditCode?: string | null;
  stageLabel?: string | null;
  resultSnapshot?: Record<string, unknown> | null;
}

export interface AnalysisRecord {
  id: number;
  userId: string;
  clientId: string;
  companyName: string;
  industry: string | null;
  region: string | null;
  opportunityScore: number | null;
  createdAt: string;
  taskId?: string | null;
  companyId?: string | null;
  creditCode?: string | null;
  stageLabel?: string | null;
  resultSnapshot?: Record<string, unknown> | null;
  trialConsumed?: boolean;
  updatedAt?: string | null;
  /** 任务状态镜像：pending | processing | completed | failed | cancelled */
  status?: TaskStatus | null;
  /** 任务进度 0-100（仅运行中有意义） */
  progress?: number | null;
  /** 状态中文文案（后端下发，避免前端重复维护映射） */
  statusLabel?: string | null;
}

export class ApiError extends Error {
  code: string;
  status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

export function readStoredAuth(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAuth;
    if (!parsed?.token || !parsed?.user?.id) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeStoredAuth(value: StoredAuth): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

export function clearStoredAuth(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function authHeaders(): Record<string, string> {
  const stored = readStoredAuth();
  if (stored?.token) {
    return { Authorization: `Bearer ${stored.token}` };
  }
  return {};
}

async function readApiError(res: Response, fallback: string): Promise<ApiError> {
  const body = await res.json().catch(() => ({}));
  const message = body?.error?.message || fallback;
  const code = body?.error?.code || 'UNKNOWN';
  return new ApiError(message, code, res.status);
}

export async function sendSmsCode(phone: string): Promise<SmsSendResult> {
  const res = await fetch(`${API_BASE_URL}/api/v1/auth/sms/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone }),
  });
  if (!res.ok) throw await readApiError(res, '验证码发送失败，请稍后重试');
  const json = await res.json();
  return json.data as SmsSendResult;
}

export async function loginWithSms(phone: string, code: string): Promise<StoredAuth> {
  const res = await fetch(`${API_BASE_URL}/api/v1/auth/sms/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone, code }),
  });
  if (!res.ok) throw await readApiError(res, '登录失败，请稍后重试');
  const json = await res.json();
  return json.data as StoredAuth;
}

export async function createSession(phone: string, password: string): Promise<StoredAuth> {
  const res = await fetch(`${API_BASE_URL}/api/v1/auth/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone, password }),
  });
  if (!res.ok) throw await readApiError(res, '登录失败，请稍后重试');
  const json = await res.json();
  return json.data as StoredAuth;
}

export async function fetchMe(
  token: string,
  opts?: { timeoutMs?: number; signal?: AbortSignal },
): Promise<AuthUser | null> {
  const timeoutMs = opts?.timeoutMs ?? 8000;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  const onOuterAbort = () => controller.abort();
  opts?.signal?.addEventListener('abort', onOuterAbort);
  try {
    const res = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
    if (res.status === 401) return null;
    if (!res.ok) throw new Error('登录状态校验失败');
    const json = await res.json();
    return json.data as AuthUser;
  } finally {
    window.clearTimeout(timer);
    opts?.signal?.removeEventListener('abort', onOuterAbort);
  }
}

export async function deleteSession(): Promise<void> {
  const stored = readStoredAuth();
  if (!stored) return;
  await fetch(`${API_BASE_URL}/api/v1/auth/session`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${stored.token}` },
  }).catch(() => undefined);
}

export async function listAnalysisRecords(): Promise<AnalysisRecord[]> {
  const res = await fetch(`${API_BASE_URL}/api/v1/me/analysis-records`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw await readApiError(res, '加载分析记录失败，请稍后重试');
  const json = await res.json();
  return (json.data as AnalysisRecord[]) ?? [];
}

export async function upsertAnalysisRecord(input: AnalysisRecordInput): Promise<void> {
  if (!readStoredAuth()) return;
  await fetch(`${API_BASE_URL}/api/v1/me/analysis-records`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(input),
  }).catch(() => undefined);
}

export async function deleteAnalysisRecord(id: number): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/api/v1/me/analysis-records/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw await readApiError(res, '删除失败，请稍后重试');
}
